<script setup>
import DaySelector from '@/components/workSchedule/DaySelector.vue';
import { isWithinInterval, parseISO, areIntervalsOverlapping } from 'date-fns';
import { toDate } from 'date-fns-tz';
import constants from '@/utils/constants';
import { isEmpty } from 'lodash';
import { ref, computed, onUnmounted, onBeforeMount } from 'vue';
import { useRoute } from 'vue-router';
import { format } from '@/utils/date-fns';
import { useDepartmentStore } from '@/stores/department';
import { useWorkScheduleStore } from '@/stores/workSchedule';
import { useCacheStore } from '@/stores/cache';
import { getOptimizedImageSrc } from '@/directives/optimizedImageSize';
import { useColorClass } from '@/composables/getColorClass';
import WorkScheduleEntryOverlay from '@/views/workSchedule/WorkScheduleEntryOverlay.vue';

const departmentStore = useDepartmentStore();
const workScheduleStore = useWorkScheduleStore();
const cache = useCacheStore();
const route = useRoute();
const showWorkScheduleEntryOverlay = ref(false);

const icon = ref({
  morgen: 'sunrise',
  eftermiddag: 'sun',
  aften: 'sunset',
  nat: 'moon-stars',
});

const selectedDay = ref(new Date());
const today = ref(new Date());

onBeforeMount(() => {
  cache.use(
    'workScheduleStore.getWorkSchedules',
    workScheduleStore.getWorkSchedules
  );

  if (!isEmpty(workSchedule.value)) {
    scrollToInterval();
  }
});

onUnmounted(() => {
  setTimeout(() => {
    workScheduleStore.setSelectedShift({});
  }, 300); // wait for overlay to close before resetting store value
});

const workSchedule = computed(() => {
  return workScheduleStore.all;
});

const selectedShift = computed(() => {
  return workScheduleStore.selectedShift;
});

const moduleColor = computed(() => {
  return route.meta.color;
});

const intervals = computed(() => {
  return departmentStore.settings.simpleSchedulePlan
    .toSorted((a, b) => new Date(a.starttime) - new Date(b.starttime))
    .map((interval) => {
      const formattedSelectedDay = format(selectedDay.value, 'yyyy-MM-dd');

      const intervalStartDateTime = new Date(
        format(interval.starttime, `${formattedSelectedDay} HH:mm:00`)
      );

      const intervalEndDateTime = new Date(
        format(interval.endtime, `${formattedSelectedDay} HH:mm:00`)
      );

      return {
        ...interval,
        shifts: getShiftsInInterval(intervalStartDateTime, intervalEndDateTime),
        active: isWithinInterval(new Date(), {
          start: interval.starttime,
          end: interval.endtime,
        }),
      };
    });
});

function getShiftsInInterval(start, end) {
  return workSchedule.value.filter((shift) => {
    const shiftStartDay = format(shift.start, 'yyyy-MM-dd');
    const shiftEndDay = format(shift.end, 'yyyy-MM-dd');
    const formattedSelectedDay = format(selectedDay.value, 'yyyy-MM-dd');

    if (
      shiftStartDay !== formattedSelectedDay &&
      shiftEndDay !== formattedSelectedDay
    )
      return false;

    const shiftStart = toDate(shift.start, { timeZone: constants.timezone });
    const shiftEnd = toDate(shift.end, { timeZone: constants.timezone });

    return areIntervalsOverlapping(
      { start: new Date(shiftStart), end: new Date(shiftEnd) },
      { start: new Date(start), end: new Date(end) }
    );
  });
}

function getLabelColors(interval) {
  return isNowWithinInterval(interval.starttime, interval.endtime)
    ? `${useColorClass(moduleColor.value).bg} text-white`
    : 'bg-white';
}

function isNowWithinInterval(startSetting, endSetting) {
  return isWithinInterval(today.value, {
    start: parseISO(startSetting),
    end: parseISO(endSetting),
  });
}

function showSingleShift(shift) {
  workScheduleStore.setSelectedShift(shift);
  showWorkScheduleEntryOverlay.value = true;
}

function scrollToInterval() {
  const element = document.querySelector('#scrollHere');
  if (!element) return;

  element.scrollIntoView({ behavior: 'smooth' });
}

function handleSelectedDay(payload) {
  selectedDay.value = payload;
}
</script>

<template>
  <div class="flex flex-col pb-48 h-initial min-h-full">
    <div
      v-for="interval in intervals"
      :id="
        isNowWithinInterval(interval.starttime, interval.endtime)
          ? 'scrollHere'
          : ''
      "
      :key="interval.name"
      class="flex flex-col justify-center items-center bg-neutral-0 mt-16 mx-16 p-8 rounded-xl bg-opacity-5"
    >
      <div
        class="w-max rounded-lg shadow-lg px-12 py-5 text-3xl font-semibold"
        :class="getLabelColors(interval)"
      >
        <fa-icon class="pr-1" :icon="['far', `${icon[interval.icon]}`]" />
        {{ interval.name }}
      </div>
      <div
        class="flex flex-wrap pt-12 pb-6 space-between justify-center grid-cols-3 grow"
      >
        <div
          v-for="(shift, index) in interval.shifts"
          :key="index"
          class="relative m-2"
        >
          <img
            alt="Employee"
            class="w-36 h-36 rounded-full border-8 shadow-m border-white object-cover"
            :class="
              interval.active && !shift.cancelled ? '' : 'filter grayscale'
            "
            @click="showSingleShift(shift)"
            :src="getOptimizedImageSrc(shift.employee.displayImage, 'small')"
          />

          <div
            v-if="interval.active"
            :class="useColorClass(moduleColor).bg"
            class="absolute right-0 bottom-1 inline-flex rounded-full h-12 w-12 border-4 border-white"
          />
          <div
            v-if="interval.active && shift.cancelled"
            class="bg-warning absolute w-12 h-12 rounded-full flex items-center justify-center right-0 bottom-1 border-4 border-white"
          >
            <fa-icon class="text-white text-xl" :icon="['fas', 'times']" />
          </div>
        </div>
      </div>
    </div>

    <DaySelector
      class="fixed bottom-80"
      @handle-selected-day="handleSelectedDay"
    />
  </div>

  <WorkScheduleEntryOverlay
    v-if="showWorkScheduleEntryOverlay"
    :title="selectedShift.employee.displayName"
    :label="selectedShift.label"
    :shift="selectedShift"
    @close="showWorkScheduleEntryOverlay = false"
  />
</template>

<style>
.h-initial {
  height: initial !important;
}
</style>
